import { usePathname } from "@/i18n/routing"
// @deprecated The embedded frontend webphone is disabled. The Chrome extension now provides the webphone sidebar.
// import { useAppStoreContext } from "@/stores/appStore"
import consola from "consola"
import { useRouter } from "@bprogress/next/app"
import { useCallback, useEffect } from "react"
import {
  EmbeddedEventKeyPressedPayloadSchema,
  EmbeddedEventLayoutPayloadSchema,
  EmbeddedEventNavigationPayloadSchema,
  EmbeddedEventNotificationPayloadSchema,
  EmbeddedEventSchema,
  type EmbeddedEvent,
  type EmbeddedEventKeyPressedPayload,
  type EmbeddedEventNavigationPayload,
  type EmbeddedEventType,
  type EmbeddedEventLayoutPayload,
  type EmbeddedEventNotificationPayload,
} from "@/hooks/embeddedEvents.helpers"

type DisallowEmpty<T> = keyof T extends never ? never : T

/**
 * Custom hook to handle embedded events received via the `message` event.
 *
 * This hook sets up an event listener for `message` events on the `window` object.
 * It validates and processes the events based on their type and payload.
 *
 * The supported event types are:
 * - `navigation`: Navigates to a specified link.
 * - `layout`: Changes the layout of the embedded page.
 * - `extensionInstalled`: Notifies that an extension has been installed.
 * - `extensionUninstalled`: Notifies that an extension has been uninstalled.
 *
 * The event data is validated using predefined schemas:
 * - `EmbeddedEventSchema` for the overall event structure.
 * - `EmbeddedEventNavigationPayloadSchema` for the navigation event payload.
 * - `EmbeddedEventLayoutPayloadSchema` for the layout event payload.
 *
 * If the event or its payload is invalid, an error is logged using `consola.error`.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.callbacks - The event callbacks. The keys are the event types and the values are the callback functions.
 * @returns {void}
 * @example
 * ```tsx
 * // Code in the embedded page
 * window.parent.postMessage({
 *  from: "ringio-webphone",
 *  type: "navigation",
 *  data: {
 *    link: "/messenger",
 *  },
 * }, "*");
 * ```
 */
export function useEmbeddedEvents(
  params?: DisallowEmpty<{
    callbacks?: Partial<Record<EmbeddedEventType, (data: EmbeddedEvent) => void>>
  }>
) {
  // @deprecated The embedded frontend webphone is disabled. Layout changes from embedded events no longer mutate webphone state.
  // const webphoneExpanded = useAppStoreContext((s) => s.webphoneExpanded)
  const router = useRouter()
  // const setWebphoneExpanded = useAppStoreContext((s) => s.setWebphoneExpanded)
  const pathname = usePathname()

  const messageEventHandler = useCallback(
    async (event: MessageEvent) => {
      const isRingioOrigin = event.origin.includes("ringio.com") || event.origin.includes("ring.io")
      const isChromeExtensionOrigin = event.origin.startsWith("chrome-extension://")
      if (!isRingioOrigin && !isChromeExtensionOrigin) return

      // Validate the event
      const embeddedEvent = EmbeddedEventSchema.safeParse(event.data)
      if (!embeddedEvent.success) {
        // consola.debug("Invalid embedded event", event.data)
        return
      }

      const { callbacks } = params ?? {}

      // Handle the event
      switch (embeddedEvent.data.type) {
        case "keyPressed": {
          const keyPressedPayload = EmbeddedEventKeyPressedPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!keyPressedPayload.success) {
            consola.debug("Invalid key pressed payload", embeddedEvent.data.data)
            return
          }

          if (callbacks?.keyPressed) {
            callbacks.keyPressed(embeddedEvent.data)
          }

          break
        }
        case "navigation": {
          // Validate the navigation payload
          const navigationPayload = EmbeddedEventNavigationPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!navigationPayload.success) {
            consola.debug("Invalid navigation payload", embeddedEvent.data.data)
            return
          }

          // Navigate to the link
          router.push(navigationPayload.data.link)

          if (callbacks?.navigation) {
            callbacks.navigation(embeddedEvent.data)
          }

          break
        }
        case "layout": {
          // Validate the layout payload
          const layoutPayload = EmbeddedEventLayoutPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!layoutPayload.success) {
            consola.debug("Invalid layout payload", embeddedEvent.data.data)
            return
          }

          // @deprecated The embedded frontend webphone is disabled. The Chrome extension now owns webphone layout state.
          // switch (layoutPayload.data.webPhoneStatus) {
          //   case "expanded":
          //     setWebphoneExpanded(true)
          //     break
          //   case "collapsed":
          //     setWebphoneExpanded(false)
          //     break
          //   default:
          //     consola.debug("Unknown webphone status", layoutPayload.data.webPhoneStatus)
          //     break
          // }

          if (callbacks?.layout) {
            callbacks.layout(embeddedEvent.data)
          }

          break
        }
        case "notification": {
          const payload = EmbeddedEventNotificationPayloadSchema.safeParse(embeddedEvent.data.data)

          if (!payload.success) {
            consola.warn("Invalid notification payload", embeddedEvent.data.data)
            return
          }

          if (!("Notification" in window)) {
            consola.warn("Notification API not available")
            return
          }

          let hasPermission = false

          if (Notification.permission !== "granted" && Notification.permission !== "denied") {
            const permission = await Notification.requestPermission()
            if (permission !== "granted") {
              consola.warn("Notification permission denied")
              return
            }

            hasPermission = true
          } else if (Notification.permission === "granted") {
            hasPermission = true
          }

          if (!hasPermission) {
            return
          }

          const notification = new Notification(payload.data.title, {
            icon: payload.data.icon,
            body: payload.data.body,
            badge: payload.data.badge,
            data: payload.data.data,
            dir: payload.data.dir,
            lang: payload.data.lang,
            requireInteraction: payload.data.requireInteraction,
            silent: payload.data.silent,
            tag: payload.data.tag,
          })
          notification.onclick = () => window.focus()
        }
        default:
          consola.debug("Unknown embedded event type", embeddedEvent.data.type)
          break
      }
    },
    [params, router]
  )

  useEffect(() => {
    window.addEventListener("message", messageEventHandler)

    return () => {
      window.removeEventListener("message", messageEventHandler)
    }
  }, [messageEventHandler])
}
